import {
	Badge,
	Button,
	CloseButton,
	Group,
	SegmentedControl,
	Stack,
	Text,
	TextInput,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import {
	type AssistantStyleText,
	fillStyleText,
} from "@/locales/assistant-style";
import {
	EXPRESSIVE_FREQUENCIES,
	type ExpressiveFrequency,
	isExpressiveFrequency,
	STYLE_LIMITS,
} from "@/types/ai-assistant-style";

interface Props {
	t: AssistantStyleText;
	words: string[];
	frequency: ExpressiveFrequency;
	error?: string;
	disabled: boolean;
	onWordsChange: (words: string[]) => void;
	onFrequencyChange: (frequency: ExpressiveFrequency) => void;
}

const sameWord = (a: string, b: string) =>
	a.toLocaleLowerCase("id-ID") === b.toLocaleLowerCase("id-ID");

/** Chip kata ekspresif (tambah/hapus) + pilihan frekuensi. */
export function StyleWordsEditor({
	t,
	words,
	frequency,
	error,
	disabled,
	onWordsChange,
	onFrequencyChange,
}: Props) {
	const [draft, setDraft] = useState("");
	const candidate = draft.trim().replace(/\s+/g, " ");
	const full = words.length >= STYLE_LIMITS.expressiveWordsMax;
	const canAdd =
		!disabled &&
		!full &&
		candidate !== "" &&
		!words.some((w) => sameWord(w, candidate));

	const add = () => {
		if (!canAdd) return;
		onWordsChange([...words, candidate]);
		setDraft("");
	};

	return (
		<Stack gap={6}>
			<Text fw={600} size="sm">
				{t.words.title}
			</Text>
			<Text size="xs" c="dimmed">
				{t.words.description} {t.words.formalHint}
			</Text>
			<Group gap={6} data-style-words>
				{words.map((word) => (
					<Badge
						key={word}
						variant="light"
						size="lg"
						tt="none"
						data-style-word={word}
						rightSection={
							<CloseButton
								size="xs"
								aria-label={fillStyleText(t.words.remove, { word })}
								data-style-word-remove={word}
								disabled={disabled}
								onClick={() => onWordsChange(words.filter((w) => w !== word))}
							/>
						}
					>
						{word}
					</Badge>
				))}
			</Group>
			<Group gap="xs" align="flex-start">
				<TextInput
					style={{ flex: 1, maxWidth: 280 }}
					placeholder={t.words.placeholder}
					maxLength={STYLE_LIMITS.expressiveWordCharsMax}
					value={draft}
					onChange={(e) => setDraft(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key !== "Enter") return;
						e.preventDefault();
						add();
					}}
					error={error}
					disabled={disabled || full}
					data-style-word-input
				/>
				<Button
					variant="light"
					leftSection={<IconPlus size={14} />}
					onClick={add}
					disabled={!canAdd}
					data-style-word-add
				>
					{t.words.add}
				</Button>
				<Text size="xs" c="dimmed" pt={8}>
					{words.length}/{STYLE_LIMITS.expressiveWordsMax}
				</Text>
			</Group>
			<Group gap="xs">
				<Text size="sm">{t.words.frequency}</Text>
				<SegmentedControl
					size="xs"
					value={frequency}
					disabled={disabled}
					onChange={(v) => {
						if (isExpressiveFrequency(v)) onFrequencyChange(v);
					}}
					data={EXPRESSIVE_FREQUENCIES.map((f) => ({
						value: f,
						label: t.words.frequencies[f],
					}))}
				/>
			</Group>
		</Stack>
	);
}
